import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { API_URL } from "../Constant";
import { AUTH_USE_HTTPONLY_COOKIE } from "../config/authFlags";
import { clearCsrfToken } from "../utils/csrf";
import {
  getToken,
  setToken,
  clearAllStorage,
  purgeStoredJwtIfCookieMode,
  setUserClaimsSnapshot,
  clearUserClaimsSnapshot,
} from "../utils/authStorage";
import { fetchSessionUser, shapeDecodedJwtPayload } from "../utils/authSessionApi";

function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginInfo, setLoginInfo] = useState(null);
  /** False until cookie session probe finishes (HttpOnly mode only). */
  const [sessionRestored, setSessionRestored] = useState(!AUTH_USE_HTTPONLY_COOKIE);

  const clearLocalAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setLoginInfo(null);
    clearUserClaimsSnapshot();
    clearAllStorage();
    clearCsrfToken();
  }, []);

  const logout = useCallback(async () => {
    const accessToken = getToken();
    const body =
      accessToken && accessToken.trim()
        ? { accessToken: accessToken.trim(), token: accessToken.trim() }
        : {};
    try {
      await axios.post(`${API_URL}api/auth/logout`, body, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
    } catch (_) {
      /* always clear local auth even if API fails */
    } finally {
      clearLocalAuthState();
    }
  }, [clearLocalAuthState]);

  /**
   * @param {string|object} payload — JWT string or `{ userInfo, token? }` from Login.
   */
  const loginCallFromChild = useCallback(
    (payload) => {
      if (payload == null || payload === "") {
        clearLocalAuthState();
        return;
      }

      let userInfo;
      let runtimeToken = null;
      if (typeof payload === "string") {
        const t = payload.trim();
        if (!t || t === "null") {
          clearLocalAuthState();
          return;
        }
        if (AUTH_USE_HTTPONLY_COOKIE) {
          try {
            const decoded = jwtDecode(t);
            userInfo = shapeDecodedJwtPayload(decoded) || decoded;
            runtimeToken = t;
            setToken(t);
          } catch {
            console.error("Login token invalid");
            clearLocalAuthState();
            return;
          }
        } else {
          try {
            const decoded = jwtDecode(t);
            userInfo = shapeDecodedJwtPayload(decoded) || decoded;
            runtimeToken = t;
            setToken(t);
          } catch {
            console.error("Login token invalid");
            clearLocalAuthState();
            return;
          }
        }
      } else {
        userInfo = payload.userInfo ?? payload;
        if (!AUTH_USE_HTTPONLY_COOKIE) {
          runtimeToken =
            typeof payload.token === "string" && payload.token.trim() !== ""
              ? payload.token.trim()
              : null;
          if (runtimeToken) {
            setToken(runtimeToken);
          }
        } else {
          runtimeToken =
            typeof payload.token === "string" && payload.token.trim() !== ""
              ? payload.token.trim()
              : null;
          if (runtimeToken) {
            setToken(runtimeToken);
          } else {
            setToken("");
          }
        }
      }

      if (!userInfo || !userInfo.id || typeof userInfo.id !== "object") {
        clearLocalAuthState();
        return;
      }

      try {
        setIsAuthenticated(true);
        const sessionToken = getToken() || runtimeToken || null;
        setLoginInfo({
          userInfo,
          token: AUTH_USE_HTTPONLY_COOKIE ? sessionToken : runtimeToken || null,
        });
        if (AUTH_USE_HTTPONLY_COOKIE) {
          setUserClaimsSnapshot(userInfo);
        }
        try {
          sessionStorage.setItem("loginTime", Date.now().toString());
        } catch (_) {
          /* ignore */
        }
      } catch (error) {
        console.error("Login session invalid", error);
        clearLocalAuthState();
      }
    },
    [clearLocalAuthState]
  );

  useEffect(() => {
    if (!AUTH_USE_HTTPONLY_COOKIE) {
      const token = getToken();
      if (!token || token === "null" || token.trim() === "") {
        return;
      }
      try {
        const decoded = jwtDecode(token);
        const userInfo = shapeDecodedJwtPayload(decoded) || decoded;
        const currentTime = Date.now() / 1000;
        if (userInfo.exp && userInfo.exp < currentTime) {
          logout();
          return;
        }
        if (!userInfo?.id || typeof userInfo.id !== "object") {
          logout();
          return;
        }
        setIsAuthenticated(true);
        setLoginInfo({ userInfo, token });
      } catch (error) {
        console.error("Token validation error:", error);
        logout();
      }
      return;
    }

    purgeStoredJwtIfCookieMode();

    let cancelled = false;
    (async () => {
      try {
        const userInfo = await fetchSessionUser();
        if (cancelled) return;
        if (!userInfo?.id) {
          clearUserClaimsSnapshot();
          setIsAuthenticated(false);
          setLoginInfo(null);
          return;
        }
        const currentTime = Date.now() / 1000;
        if (userInfo.exp && userInfo.exp < currentTime) {
          await logout();
          return;
        }
        setIsAuthenticated(true);
        setLoginInfo({ userInfo, token: getToken() || null });
        setUserClaimsSnapshot(userInfo);
      } catch (error) {
        console.error("Cookie session validation error:", error);
        clearUserClaimsSnapshot();
        setIsAuthenticated(false);
        setLoginInfo(null);
      } finally {
        if (!cancelled) setSessionRestored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logout]);

  return {
    isAuthenticated,
    loginInfo,
    loginCallFromChild,
    logout,
    sessionRestored,
  };
}

export default useAuth;
